import React, { useMemo } from 'react';
import { useAssets } from '@atlassian/global-shortcuts/assets';
import { useConfluence } from '@atlassian/global-shortcuts/confluence';
import { useLoom } from '@atlassian/global-shortcuts/loom';
import { useGoals } from '@atlassian/global-shortcuts/goals';
import { useOpsgenie } from '@atlassian/global-shortcuts/opsgenie';
import { useProjects } from '@atlassian/global-shortcuts/projects';
import { L1_MENU_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { getUserNavigationItemLinks } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { GlobalAppShortcuts } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts/src/index.tsx';
import {
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

// Filter product shortcuts based on licence states
export const useLicenseFilterMap = (teamsAppConfig: GlobalShortcutsItem | undefined) => {
	const licenceStates = useLicenseStates();
	const products = getLicensedProducts();
	const hasConfluence = products[LICENSED_PRODUCTS.CONFLUENCE];
	const hasLoom = products[LICENSED_PRODUCTS.LOOM];
	let hasProject = true;
	let hasGoal = true;

	// The metadata used in getLicensedProducts() has changed from townsquare to project and goal.
	// Experiment cleaned up: always use PROJECT/GOAL metadata with TOWNSQUARE fallback
	hasProject = !!products[LICENSED_PRODUCTS.PROJECT] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];
	hasGoal = !!products[LICENSED_PRODUCTS.GOAL] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];

	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

	const confluenceItem = useConfluence(
		USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
	);
	const loomItem = useLoom(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_LOOM]);
	const assetsItem = useAssets(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_ASSETS]);
	const opsgenieItem = useOpsgenie(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_OPSGENIE]);
	const goalsItem = useGoals(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_GOALS]);
	const projectsItem = useProjects(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_PROJECTS]);

	return useMemo(() => {
		const assetsItemWithComponent = { ...assetsItem, component: AssetsComponent };

		if (licenceStates.core === 'ACTIVE') {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluenceItem } : {}),
					...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loomItem } : {}),
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: assetsItemWithComponent,
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenieItem,
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					...(hasGoal ? { [L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem } : {}),
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluenceItem } : {}),
					...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loomItem } : {}),
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					...(hasGoal ? { [L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem } : {}),
				};
			}
		} else {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: assetsItemWithComponent,
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenieItem,
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					...(hasGoal ? { [L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem } : {}),
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					...(hasGoal ? { [L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem } : {}),
				};
			}
		}

		return {
			...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluenceItem } : {}),
			...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loomItem } : {}),
			...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
			...(hasGoal ? { [L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem } : {}),
			...(hasProject ? { [L1_MENU_ID.GLOBAL_APP_PROJECTS]: projectsItem } : {}),
		};
	}, [
		assetsItem,
		confluenceItem,
		goalsItem,
		hasConfluence,
		hasLoom,
		hasGoal,
		hasProject,
		licenceStates.core,
		licenceStates.productDiscovery,
		licenceStates.serviceDesk,
		loomItem,
		opsgenieItem,
		projectsItem,
		teamsAppConfig,
	]);
};

const AssetsComponent = () => <GlobalAppShortcuts menuId={L1_MENU_ID.GLOBAL_APP_ASSETS} />;
